import React from 'react'
import cx from 'classnames'
import CreateOCRScopeJobsViewModel, {
  ACCEPTED_OCR_FILE_TYPES,
  CreateOCRScopeJobsViewModelStep,
} from './CreateOCRScopeJobsViewModel'
import { observer } from 'mobx-react'
import {
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheet,
  ModalSheetHeaderCloseButton,
  ModalSheetHeaderShell,
  ModalSheetContent,
  ModalSheetFooterSpacer,
  ModalSheetContentBlock,
} from 'components/ModalSheet'
import styles from './CreateOCRScopeJobsDialog.sass'
import { match } from 'utils/pattern-match-util'
import { IconButton } from 'components/IconButton'
import Icon from 'components/Icon'
import FileButton from 'components/FileButton'
import Button from 'components/Button'
import { Form, FormGroupContainer, FormRow } from 'components/inputs'
import { RadioButtonGroup, RadioButtonItem } from 'components/RadioButtonGroup'
import {
  CustomSelect,
  ItemSource,
  SingleItemSelectedDefault,
  useCustomSelectStateFromInfiniteScrollProvider,
  useCustomSelectStateFromItemSource,
} from 'components/inputs/CustomSelect'
import SearchResultItem from 'components/inputs/CustomSelect/SearchResultItem'
import Avatar from 'components/Avatar'
import { truncate } from 'lodash'
import TextArea from 'components/inputs/TextArea'
import NumericInput from 'components/inputs/NumericInput'
import DateInput from 'components/inputs/DateInput'
import Spinner from 'components/Spinner'
import Checkbox from 'components/Checkbox'
import {
  QuestionDTO,
  BoolQuestionDTO,
  SelectQuestionDTO,
  SelectOptionDTO,
} from '../../../../dtos/questions'

interface CreateOCRScopeJobsDialogProps {
  vm: CreateOCRScopeJobsViewModel
}

export default observer(function CreateOCRScopeJobsDialog({
  vm,
}: CreateOCRScopeJobsDialogProps) {
  return (
    <ModalSheet
      showing={vm.showing}
      disableCloseOnOutsideClick
      onRequestClose={vm.close}
    >
      {match(vm.createOCRScopeJobsViewModelStep, {
        [CreateOCRScopeJobsViewModelStep.ManageDocuments]: () => (
          <>
            <CreateOCRScopeJobsHeader
              heading="1 of 2"
              title="Manage documents"
              description="Review, add or remove files for job scoping"
            >
              <div className={styles.documentsColumnHeader}>
                <span className={styles.documentsColumnHeaderText}>
                  Documents
                </span>
                <span className={styles.documentsColumnHeaderCount}>
                  ({vm.selectedFilesCount})
                </span>
              </div>
            </CreateOCRScopeJobsHeader>
            <ManageDocumentsContent vm={vm} />
            <ModalSheetFooter>
              <div className={styles.manageDocumentsFooterButtons}>
                <FileButton
                  onChange={vm.addFiles}
                  multiple
                  accept={ACCEPTED_OCR_FILE_TYPES}
                >
                  <Button>Add more</Button>
                </FileButton>
                <ModalSheetFooterSpacer />
                <Button
                  onClick={vm.goToScopeNewJobs}
                  type="primary"
                  disabled={vm.selectedFilesCount === 0}
                >
                  Next
                </Button>
              </div>
            </ModalSheetFooter>
          </>
        ),
        [CreateOCRScopeJobsViewModelStep.ScopeNewJobs]: () => (
          <>
            <CreateOCRScopeJobsHeader
              heading="2 of 2"
              title="Scope new jobs"
              description="Set the OCR requirements for the shared files"
            />
            {vm.isNonAdminUser ? (
              <NonAdminScopeNewJobsContent vm={vm} />
            ) : (
              <ScopeNewJobsContent vm={vm} />
            )}
            <ModalSheetFooter>
              <ModalSheetFooterSpacer />
              <ModalSheetFooterButtonGroup>
                <Button onClick={vm.goToManageDocuments}>Back</Button>

                <Button
                  type="primary"
                  onClick={vm.submit}
                  loading={vm.submit.pending}
                  disabled={vm.submit.pending || vm.disabled}
                >
                  {vm.scopeJobMode === 'collectively' &&
                  vm.selectedFilesCount > 1
                    ? `Scope ${vm.selectedFilesCount} Jobs`
                    : 'Scope This Job'}
                </Button>
              </ModalSheetFooterButtonGroup>
            </ModalSheetFooter>
          </>
        ),
      })}
    </ModalSheet>
  )
})

const CreateOCRScopeJobsHeader = observer(function CreateOCRScopeJobsHeader({
  heading,
  title,
  description,
  children,
}: {
  heading: string
  title: string
  description: string
  children?: React.ReactNode
}) {
  return (
    <ModalSheetHeaderShell size="auto">
      <div className={styles.headerShell}>
        <div className={styles.closeButtonContainer}>
          <ModalSheetHeaderCloseButton />
        </div>
        <div className={styles.headerContent}>
          <div className={styles.headerHeading}>{heading}</div>
          <div className={styles.headerTitle}>{title}</div>
          <div className={styles.headerDescription}>{description}</div>
        </div>
        {children}
      </div>
    </ModalSheetHeaderShell>
  )
})

const ManageDocumentsContent = observer(function ManageDocumentsContent({
  vm,
}: {
  vm: CreateOCRScopeJobsViewModel
}) {
  return (
    <ModalSheetContent>
      <div
        className={cx(
          styles.selectedFilesContainer,
          vm.selectedFilesCount === 0 && styles.noFilesSelected,
        )}
      >
        {vm.selectedFilesCount === 0 && (
          <div className={styles.selectedFilesEmptyStateContainer}>
            <div className={styles.selectedFilesEmptyStateText}>
              Please select a file to continue
            </div>
          </div>
        )}
        {vm.selectedFiles.map((file, index) => (
          <SelectedFile file={file} vm={vm} key={index} />
        ))}
      </div>
    </ModalSheetContent>
  )
})

const ScopeNewJobsContent = observer(function ScopeNewJobsContent({
  vm,
}: {
  vm: CreateOCRScopeJobsViewModel
}) {
  const workspaceSelectProps = useCustomSelectStateFromItemSource({
    itemSource: React.useMemo(
      () =>
        ItemSource.static(vm.workspaces, (item, text) => {
          const lowerCaseText = text.toLowerCase()
          return item.name.toLowerCase().includes(lowerCaseText)
        }),
      [vm.workspaces],
    ),
  })
  const fileSelectProps = useCustomSelectStateFromItemSource({
    itemSource: React.useMemo(
      () =>
        ItemSource.static(vm.selectedFiles, (item, text) => {
          const lowerCaseText = text.toLowerCase()
          return item.name.toLowerCase().includes(lowerCaseText)
        }),
      [vm.selectedFiles],
    ),
  })
  const legendSelectProps = useCustomSelectStateFromInfiniteScrollProvider({
    scrollProvider: vm.legendScrollProvider,
    searchResults: vm.filteredLegends,
    searchText: vm.legendSearchText,
    onSearchTextChange: vm.setLegendSearchText,
  })
  const templateSelectProps = useCustomSelectStateFromItemSource({
    itemSource: React.useMemo(
      () =>
        ItemSource.static(vm.templates ?? [], (item, text) => {
          const lowerCaseText = text.toLowerCase()
          return item.name.toLowerCase().includes(lowerCaseText)
        }),
      [vm.templates],
    ),
  })
  const championClientProps = useCustomSelectStateFromInfiniteScrollProvider({
    scrollProvider: vm.championClientScrollProvider,
    searchResults: vm.championClientScrollProvider.items,
    searchText: vm.championClientSearchText.value,
    onSearchTextChange: vm.championClientSearchText.change,
  })
  const supportingClientProps = useCustomSelectStateFromInfiniteScrollProvider({
    scrollProvider: vm.supportingClientScrollProvider,
    searchResults: vm.supportingClientScrollProvider.items,
    searchText: vm.supportingClientSearchText.value,
    onSearchTextChange: vm.supportingClientSearchText.change,
  })
  const clientTeamProps = useCustomSelectStateFromInfiniteScrollProvider({
    scrollProvider: vm.clientTeamMembersScrollProvider,
    searchResults: vm.filteredClientTeam,
    searchText: vm.clientTeamSearchText,
    onSearchTextChange: vm.setClientTeamSearchText,
  })

  return (
    <ModalSheetContent>
      <ModalSheetContentBlock>
        <Form>
          <FormRow>
            {!vm.isOnlyOneFileSelected && (
              <RadioButtonGroup
                size="big"
                label="How do you want to scope uploaded documents?"
              >
                <RadioButtonItem
                  onClick={() => vm.onChangeScopeJobMode('collectively')}
                  active={vm.scopeJobMode === 'collectively'}
                >
                  Collectively
                </RadioButtonItem>
                <RadioButtonItem
                  onClick={() => vm.onChangeScopeJobMode('individually')}
                  active={vm.scopeJobMode === 'individually'}
                >
                  Individually
                </RadioButtonItem>
              </RadioButtonGroup>
            )}
          </FormRow>

          {vm.scopeJobMode === 'individually' && (
            <FormRow>
              <FormGroupContainer
                label="Uploaded documents"
                help="Selected document will be scoped based on answers below."
                error={vm.validation.getError('selectedFile')}
              >
                <CustomSelect
                  {...fileSelectProps}
                  icon={<Icon name="dropdown" className={styles.icon} />}
                  itemToString={(i) => truncate(i.name, { length: 60 })}
                  value={vm.selectedFile}
                  multi={false}
                  onChange={vm.setSelectedFile}
                  renderItem={(itemProps) => (
                    <SingleItemSelectedDefault
                      {...itemProps}
                      item={itemProps}
                      itemToString={(i) =>
                        truncate(i.item?.name, { length: 60 })
                      }
                    />
                  )}
                  renderSearchItem={({ item, itemToString, ...itemProps }) => (
                    <SearchResultItem {...itemProps}>
                      {item.name}
                    </SearchResultItem>
                  )}
                />
              </FormGroupContainer>
            </FormRow>
          )}

          {vm.workspaces.length > 1 && (
            <FormRow>
              <FormGroupContainer
                label="Workspace"
                error={vm.validation.getError('workspace')}
              >
                <CustomSelect
                  {...workspaceSelectProps}
                  placeholder="Select a workspace"
                  icon={<Icon name="dropdown" className={styles.icon} />}
                  itemToString={(v) => String(v.name)}
                  value={vm.workspace}
                  multi={false}
                  onChange={vm.setWorkspace}
                  renderItem={(itemProps) => (
                    <SingleItemSelectedDefault
                      {...itemProps}
                      visual={
                        <Avatar
                          size={24}
                          subject={{
                            displayName: itemProps.item.name,
                            avatar: itemProps.item.branding.logos.icon,
                          }}
                        />
                      }
                    />
                  )}
                  renderSearchItem={({ item, itemToString, ...itemProps }) => (
                    <SearchResultItem
                      {...itemProps}
                      visual={
                        <Avatar
                          size={24}
                          subject={{
                            avatar: item.branding.logos.icon,
                            displayName: item.name,
                          }}
                        />
                      }
                    >
                      {item.name}
                    </SearchResultItem>
                  )}
                />
              </FormGroupContainer>
            </FormRow>
          )}

          {vm.canSelectLegend && (
            <FormRow>
              <FormGroupContainer
                label="Legend"
                error={vm.validation.getError('legend')}
              >
                <CustomSelect
                  {...legendSelectProps}
                  placeholder="Select a legend"
                  icon={<Icon name="dropdown" className={styles.icon} />}
                  itemToString={(v) => String(v.name)}
                  value={vm.legend}
                  multi={false}
                  onChange={vm.setLegend}
                  renderItem={(itemProps) => (
                    <SingleItemSelectedDefault
                      {...itemProps}
                      item={itemProps}
                      itemToString={(i) => String(i.item?.name)}
                    />
                  )}
                  renderSearchItem={({ item, itemToString, ...itemProps }) => (
                    <SearchResultItem {...itemProps}>
                      {item.name}
                    </SearchResultItem>
                  )}
                />
              </FormGroupContainer>
            </FormRow>
          )}

          {vm.canSelectTemplate && (
            <FormRow>
              <FormGroupContainer
                label="Template"
                error={vm.validation.getError('selectedTemplate')}
              >
                <CustomSelect
                  {...templateSelectProps}
                  placeholder="Select a template"
                  icon={<Icon name="dropdown" className={styles.icon} />}
                  itemToString={(v) => String(v.name)}
                  value={vm.selectedTemplate}
                  multi={false}
                  onChange={vm.setSelectedTemplate}
                  renderItem={(itemProps) => (
                    <SingleItemSelectedDefault
                      {...itemProps}
                      item={itemProps}
                      itemToString={(i) => String(i.item?.name)}
                    />
                  )}
                  renderSearchItem={({ item, itemToString, ...itemProps }) => (
                    <SearchResultItem {...itemProps}>
                      {item.name}
                    </SearchResultItem>
                  )}
                />
              </FormGroupContainer>
            </FormRow>
          )}

          {vm.workspace && (
            <FormRow>
              <FormGroupContainer
                label="Champion client"
                error={vm.validation.getError('championClient')}
              >
                <CustomSelect
                  {...championClientProps}
                  multi={false}
                  icon={<Icon name="dropdown" className={styles.icon} />}
                  renderItem={(itemProps) => (
                    <SingleItemSelectedDefault
                      {...itemProps}
                      item={itemProps}
                      itemToString={(i) => String(i.item?.displayName)}
                      visual={<Avatar size={24} subject={itemProps.item} />}
                    />
                  )}
                  placeholder="Select a champion client"
                  value={vm.championClient}
                  onChange={vm.setChampionClient}
                  renderSearchItem={({ item, itemToString, ...itemProps }) => (
                    <SearchResultItem
                      {...itemProps}
                      visual={
                        <Avatar
                          size={24}
                          subject={{
                            avatar: item.avatar,
                            displayName: item.displayName,
                          }}
                        />
                      }
                    >
                      {item.displayName}
                    </SearchResultItem>
                  )}
                />
              </FormGroupContainer>
            </FormRow>
          )}

          {vm.canSelectSupportingClientAsAdminUser && (
            <FormRow>
              <FormGroupContainer label="Supporting client">
                <CustomSelect
                  {...supportingClientProps}
                  placeholder="Select a supporting client, if needed"
                  multi={false}
                  icon={<Icon name="dropdown" className={styles.icon} />}
                  renderItem={(itemProps) => (
                    <SingleItemSelectedDefault
                      {...itemProps}
                      item={itemProps}
                      itemToString={(i) => String(i.item?.displayName)}
                      visual={<Avatar size={24} subject={itemProps.item} />}
                    />
                  )}
                  value={vm.supportingClient}
                  onChange={vm.setSupportingClient}
                  renderSearchItem={({ item, itemToString, ...itemProps }) => (
                    <SearchResultItem
                      {...itemProps}
                      visual={
                        <Avatar
                          size={24}
                          subject={{
                            avatar: item.avatar,
                            displayName: item.displayName,
                          }}
                        />
                      }
                    >
                      {item.displayName}
                    </SearchResultItem>
                  )}
                />
              </FormGroupContainer>
            </FormRow>
          )}

          {vm.canSelectClientTeam && (
            <FormRow>
              <FormGroupContainer label="Team">
                <CustomSelect
                  {...clientTeamProps}
                  placeholder="Select a client team, if needed"
                  multi={false}
                  icon={<Icon name="dropdown" className={styles.icon} />}
                  itemToString={(i) => truncate(i.team.name, { length: 60 })}
                  value={vm.clientTeamMember}
                  onChange={vm.setClientTeamMember}
                  renderSearchItem={({ item, itemToString, ...itemProps }) => (
                    <SearchResultItem {...itemProps}>
                      {item.team.name}
                    </SearchResultItem>
                  )}
                />
              </FormGroupContainer>
            </FormRow>
          )}

          {vm.canSelectManualQuestions && (
            <FormRow>
              <FormGroupContainer>
                {vm.manualQuestions?.map((question, i) => (
                  <OCRManualQuestionsInput
                    vm={vm}
                    question={question}
                    key={i}
                  />
                ))}
              </FormGroupContainer>
            </FormRow>
          )}

          {vm.canSaveAnswers && (
            <FormRow>
              <FormGroupContainer>
                <div className={styles.saveAnswersContainer}>
                  <Checkbox
                    checked={vm.saveAnswers}
                    onChange={vm.toggleSaveAnswers}
                  />
                  <span className={styles.saveAnswersText}>
                    Apply answers to all jobs
                  </span>
                </div>
              </FormGroupContainer>
            </FormRow>
          )}
        </Form>
      </ModalSheetContentBlock>

      {vm.isFetchingManualQuestions && (
        <div className={styles.manualQuestionsSpinner}>
          <Spinner />
        </div>
      )}
    </ModalSheetContent>
  )
})

const NonAdminScopeNewJobsContent = observer(
  function NonAdminScopeNewJobsContent({
    vm,
  }: {
    vm: CreateOCRScopeJobsViewModel
  }) {
    const workspaceSelectProps = useCustomSelectStateFromItemSource({
      itemSource: React.useMemo(
        () =>
          ItemSource.static(vm.workspaces, (item, text) => {
            const lowerCaseText = text.toLowerCase()
            return item.name.toLowerCase().includes(lowerCaseText)
          }),
        [vm.workspaces],
      ),
    })
    const fileSelectProps = useCustomSelectStateFromItemSource({
      itemSource: React.useMemo(
        () =>
          ItemSource.static(vm.selectedFiles, (item, text) => {
            const lowerCaseText = text.toLowerCase()
            return item.name.toLowerCase().includes(lowerCaseText)
          }),
        [vm.selectedFiles],
      ),
    })
    const legendSelectProps = useCustomSelectStateFromInfiniteScrollProvider({
      scrollProvider: vm.legendScrollProvider,
      searchResults: vm.filteredLegends,
      searchText: vm.legendSearchText,
      onSearchTextChange: vm.setLegendSearchText,
    })
    const templateSelectProps = useCustomSelectStateFromItemSource({
      itemSource: React.useMemo(
        () =>
          ItemSource.static(vm.templates ?? [], (item, text) => {
            const lowerCaseText = text.toLowerCase()
            return item.name.toLowerCase().includes(lowerCaseText)
          }),
        [vm.templates],
      ),
    })
    const clientTeamProps = useCustomSelectStateFromInfiniteScrollProvider({
      scrollProvider: vm.clientTeamMembersScrollProvider,
      searchResults: vm.filteredClientTeam,
      searchText: vm.clientTeamSearchText,
      onSearchTextChange: vm.setClientTeamSearchText,
    })
    const championClientTeamMembershipsProps =
      useCustomSelectStateFromInfiniteScrollProvider({
        scrollProvider: vm.championClientTeamMembershipsScrollProvider,
        searchResults: vm.filteredChampionClientTeamMemberships,
        searchText: vm.championClientTeamMembershipsSearchText,
        onSearchTextChange: vm.setChampionClientTeamMembershipsSearchText,
      })
    const championClientTeamMembersProps =
      useCustomSelectStateFromInfiniteScrollProvider({
        scrollProvider: vm.championClientTeamMembersScrollProvider,
        searchResults: vm.filteredChampionClientTeamMembers,
        searchText: vm.championClientTeamMembersSearchText,
        onSearchTextChange: vm.setChampionClientTeamMembersSearchText,
      })
    const supportingTeamMembersProps =
      useCustomSelectStateFromInfiniteScrollProvider({
        scrollProvider: vm.championClientTeamMembersScrollProvider,
        searchResults: vm.filteredSupportingTeamMembers,
        searchText: vm.supportingTeamMembersSearchText,
        onSearchTextChange: vm.setSupportingTeamMembersSearchText,
      })

    return (
      <ModalSheetContent>
        <ModalSheetContentBlock>
          <Form>
            <FormRow>
              {!vm.isOnlyOneFileSelected && (
                <RadioButtonGroup
                  size="big"
                  label="How do you want to scope uploaded documents?"
                >
                  <RadioButtonItem
                    onClick={() => vm.onChangeScopeJobMode('collectively')}
                    active={vm.scopeJobMode === 'collectively'}
                  >
                    Collectively
                  </RadioButtonItem>
                  <RadioButtonItem
                    onClick={() => vm.onChangeScopeJobMode('individually')}
                    active={vm.scopeJobMode === 'individually'}
                  >
                    Individually
                  </RadioButtonItem>
                </RadioButtonGroup>
              )}
            </FormRow>

            {vm.scopeJobMode === 'individually' && (
              <FormRow>
                <FormGroupContainer
                  label="Uploaded documents"
                  help="Selected document will be scoped based on answers below."
                  error={vm.validation.getError('selectedFile')}
                >
                  <CustomSelect
                    {...fileSelectProps}
                    icon={<Icon name="dropdown" className={styles.icon} />}
                    itemToString={(i) => truncate(i.name, { length: 60 })}
                    value={vm.selectedFile}
                    multi={false}
                    onChange={vm.setSelectedFile}
                    renderItem={(itemProps) => (
                      <SingleItemSelectedDefault
                        {...itemProps}
                        item={itemProps}
                        itemToString={(i) =>
                          truncate(i.item?.name, { length: 60 })
                        }
                      />
                    )}
                    renderSearchItem={({
                      item,
                      itemToString,
                      ...itemProps
                    }) => (
                      <SearchResultItem {...itemProps}>
                        {item.name}
                      </SearchResultItem>
                    )}
                  />
                </FormGroupContainer>
              </FormRow>
            )}

            {vm.workspaces.length > 1 && (
              <FormRow>
                <FormGroupContainer
                  label="Workspace"
                  error={vm.validation.getError('workspace')}
                >
                  <CustomSelect
                    {...workspaceSelectProps}
                    placeholder="Select a workspace"
                    icon={<Icon name="dropdown" className={styles.icon} />}
                    itemToString={(v) => String(v.name)}
                    value={vm.workspace}
                    multi={false}
                    onChange={vm.setWorkspace}
                    renderItem={(itemProps) => (
                      <SingleItemSelectedDefault
                        {...itemProps}
                        visual={
                          <Avatar
                            size={24}
                            subject={{
                              displayName: itemProps.item.name,
                              avatar: itemProps.item.branding.logos.icon,
                            }}
                          />
                        }
                      />
                    )}
                    renderSearchItem={({
                      item,
                      itemToString,
                      ...itemProps
                    }) => (
                      <SearchResultItem
                        {...itemProps}
                        visual={
                          <Avatar
                            size={24}
                            subject={{
                              avatar: item.branding.logos.icon,
                              displayName: item.name,
                            }}
                          />
                        }
                      >
                        {item.name}
                      </SearchResultItem>
                    )}
                  />
                </FormGroupContainer>
              </FormRow>
            )}

            {vm.canSelectLegend && (
              <FormRow>
                <FormGroupContainer
                  label="Legend"
                  error={vm.validation.getError('legend')}
                >
                  <CustomSelect
                    {...legendSelectProps}
                    placeholder="Select a legend"
                    icon={<Icon name="dropdown" className={styles.icon} />}
                    itemToString={(v) => String(v.name)}
                    value={vm.legend}
                    multi={false}
                    onChange={vm.setLegend}
                    renderItem={(itemProps) => (
                      <SingleItemSelectedDefault
                        {...itemProps}
                        item={itemProps}
                        itemToString={(i) => String(i.item?.name)}
                      />
                    )}
                    renderSearchItem={({
                      item,
                      itemToString,
                      ...itemProps
                    }) => (
                      <SearchResultItem {...itemProps}>
                        {item.name}
                      </SearchResultItem>
                    )}
                  />
                </FormGroupContainer>
              </FormRow>
            )}

            {vm.canSelectTemplate && (
              <FormRow>
                <FormGroupContainer
                  label="Template"
                  error={vm.validation.getError('selectedTemplate')}
                >
                  <CustomSelect
                    {...templateSelectProps}
                    placeholder="Select a template"
                    icon={<Icon name="dropdown" className={styles.icon} />}
                    itemToString={(v) => String(v.name)}
                    value={vm.selectedTemplate}
                    multi={false}
                    onChange={vm.setSelectedTemplate}
                    renderItem={(itemProps) => (
                      <SingleItemSelectedDefault
                        {...itemProps}
                        item={itemProps}
                        itemToString={(i) => String(i.item?.name)}
                      />
                    )}
                    renderSearchItem={({
                      item,
                      itemToString,
                      ...itemProps
                    }) => (
                      <SearchResultItem {...itemProps}>
                        {item.name}
                      </SearchResultItem>
                    )}
                  />
                </FormGroupContainer>
              </FormRow>
            )}

            {vm.canSelectTeamAsNonAdminUser && (
              <FormRow>
                <FormGroupContainer label="Team">
                  <CustomSelect
                    {...championClientTeamMembershipsProps}
                    placeholder="To scope a job for team members, select a team"
                    multi={false}
                    icon={<Icon name="dropdown" className={styles.icon} />}
                    itemToString={(i) => truncate(i.name, { length: 60 })}
                    value={vm.selectedNonAdminUserTeam}
                    onChange={vm.setSelectedNonAdminUserTeam}
                    renderSearchItem={({
                      item,
                      itemToString,
                      ...itemProps
                    }) => (
                      <SearchResultItem {...itemProps}>
                        {item.name}
                      </SearchResultItem>
                    )}
                  />
                </FormGroupContainer>
              </FormRow>
            )}

            {vm.workspace && (
              <FormRow>
                <FormGroupContainer
                  label="Champion client"
                  error={vm.validation.getError('championClient')}
                >
                  <CustomSelect
                    {...championClientTeamMembersProps}
                    placeholder="Select a champion client"
                    multi={false}
                    icon={<Icon name="dropdown" className={styles.icon} />}
                    renderItem={(itemProps) => (
                      <SingleItemSelectedDefault
                        {...itemProps}
                        item={itemProps}
                        itemToString={(i) => String(i.item?.displayName)}
                        visual={<Avatar size={24} subject={itemProps.item} />}
                      />
                    )}
                    value={vm.championClient}
                    onChange={vm.setChampionClient}
                    renderSearchItem={({
                      item,
                      itemToString,
                      ...itemProps
                    }) => (
                      <SearchResultItem
                        {...itemProps}
                        visual={
                          <Avatar
                            size={24}
                            subject={{
                              avatar: item.avatar,
                              displayName: item.displayName,
                            }}
                          />
                        }
                      >
                        {item.displayName}
                      </SearchResultItem>
                    )}
                  />
                </FormGroupContainer>
              </FormRow>
            )}

            {vm.canSelectSupportingClientAsNonAdminUser && (
              <FormRow>
                <FormGroupContainer label="Supporting client">
                  <CustomSelect
                    {...supportingTeamMembersProps}
                    placeholder="Select a supporting client, if needed"
                    multi={false}
                    icon={<Icon name="dropdown" className={styles.icon} />}
                    renderItem={(itemProps) => (
                      <SingleItemSelectedDefault
                        {...itemProps}
                        item={itemProps}
                        itemToString={(i) => String(i.item?.displayName)}
                        visual={<Avatar size={24} subject={itemProps.item} />}
                      />
                    )}
                    value={vm.supportingClient}
                    onChange={vm.setSupportingClient}
                    renderSearchItem={({
                      item,
                      itemToString,
                      ...itemProps
                    }) => (
                      <SearchResultItem
                        {...itemProps}
                        visual={
                          <Avatar
                            size={24}
                            subject={{
                              avatar: item.avatar,
                              displayName: item.displayName,
                            }}
                          />
                        }
                      >
                        {item.displayName}
                      </SearchResultItem>
                    )}
                  />
                </FormGroupContainer>
              </FormRow>
            )}

            {vm.canSelectClientTeam && (
              <FormRow>
                <FormGroupContainer label="Team">
                  <CustomSelect
                    {...clientTeamProps}
                    placeholder="Select a client team, if needed"
                    multi={false}
                    icon={<Icon name="dropdown" className={styles.icon} />}
                    itemToString={(i) => truncate(i.team.name, { length: 60 })}
                    value={vm.clientTeamMember}
                    onChange={vm.setClientTeamMember}
                    renderSearchItem={({
                      item,
                      itemToString,
                      ...itemProps
                    }) => (
                      <SearchResultItem {...itemProps}>
                        {item.team.name}
                      </SearchResultItem>
                    )}
                  />
                </FormGroupContainer>
              </FormRow>
            )}

            {vm.canSelectManualQuestions && (
              <FormRow>
                <FormGroupContainer>
                  {vm.manualQuestions?.map((question, i) => (
                    <OCRManualQuestionsInput
                      vm={vm}
                      question={question}
                      key={i}
                    />
                  ))}
                </FormGroupContainer>
              </FormRow>
            )}

            {vm.canSaveAnswers && (
              <FormRow>
                <FormGroupContainer>
                  <div className={styles.saveAnswersContainer}>
                    <Checkbox
                      checked={vm.saveAnswers}
                      onChange={vm.toggleSaveAnswers}
                    />
                    <span className={styles.saveAnswersText}>
                      Apply answers to all jobs
                    </span>
                  </div>
                </FormGroupContainer>
              </FormRow>
            )}
          </Form>
        </ModalSheetContentBlock>

        {vm.isFetchingManualQuestions && (
          <div className={styles.manualQuestionsSpinner}>
            <Spinner />
          </div>
        )}
      </ModalSheetContent>
    )
  },
)

const SelectedFile = observer(function SelectedFile({
  file,
  vm,
}: {
  file: File
  vm: CreateOCRScopeJobsViewModel
}) {
  return (
    <div className={styles.selectedFile}>
      <div className={styles.selectedFileName}>{file.name}</div>
      <IconButton
        className={styles.unselectFileButton}
        onClick={() => vm.removeFile(file)}
      >
        <Icon name="close" />
      </IconButton>
    </div>
  )
})

export const CreateOCRScopeJobsDraggingOverlay = () => {
  return (
    <div className={styles.dragAndDropOverlay}>
      <img
        className={styles.dragAndDropImage}
        src={require('assets/images/img_drag-drop.svg')}
      />
      <h2 className={styles.dragAndDropTitle}>
        Drag and drop to scope new jobs
      </h2>
      <p className={styles.dragAndDropDescription}>
        Upload one or more files here. Acceptable file types include: BMP, JPEG,
        JPG, PDF, PNG, TIF, TIFF, WEBP
      </p>
    </div>
  )
}

export const OCRManualQuestionsInput = observer(
  function OCRManualQuestionsInput({
    vm,
    question,
  }: {
    vm: CreateOCRScopeJobsViewModel
    question: QuestionDTO
  }) {
    switch (question.type) {
      case 'BOOL':
        return (
          <>
            <div className={styles.questionTitle}>{question.question}</div>
            <OCRManualQuestionsBoolInput vm={vm} question={question} />
          </>
        )
      case 'SELECT':
        return (
          <>
            <div className={styles.questionTitle}>{question.question}</div>
            <OCRManualQuestionsSelectInput vm={vm} selectQuestion={question} />
          </>
        )
      case 'TEXT':
        return (
          <>
            <div className={styles.questionTitle}>{question.question}</div>
            <TextArea
              autoSize
              rows={1}
              placeholder="Enter value here"
              value={
                vm.answers?.find((q) => q.id === question.id)?.answer as string
              }
              onChange={(e) => vm.updateAnswer(e.target.value, question.id)}
            />
          </>
        )
      case 'NUMERIC':
        return (
          <>
            <div className={styles.questionTitle}>{question.question}</div>
            <NumericInput
              value={
                vm.answers?.find((q) => q.id === question.id)?.answer as number
              }
              precision={
                question.format?.type === 'Decimal' ? question.format.places : 0
              }
              placeholder="Enter value here"
              onChange={(value) => vm.updateAnswer(value, question.id)}
            />
          </>
        )
      case 'DATE_TIME': {
        const answer = vm.answers?.find((q) => q.id === question.id)?.answer
        return (
          <>
            <div className={styles.questionTitle}>{question.question}</div>
            <DateInput
              value={
                answer instanceof Date ? answer : new Date(answer as string)
              }
              onChange={(value) => vm.updateAnswer(value, question.id)}
              includeTime={question.collect_time}
              icon="dropdown"
            />
          </>
        )
      }
      case 'DOCUMENTS':
      case 'BREAKER':
        return undefined
    }
  },
)

const OCRManualQuestionsBoolInput = observer(
  function OCRManualQuestionsBoolInput({
    vm,
    question,
  }: {
    vm: CreateOCRScopeJobsViewModel
    question: BoolQuestionDTO
  }) {
    const itemSource = React.useMemo(() => {
      return ItemSource.static<string>(['Yes', 'No'], (item, text) =>
        item.toLowerCase().includes(text.toLowerCase()),
      )
    }, [])
    const itemToString = React.useCallback((itemName: string) => itemName, [])
    const selectProps = useCustomSelectStateFromItemSource({
      itemSource,
    })

    return (
      <CustomSelect
        {...selectProps}
        multi={false}
        icon={<Icon name="dropdown" className={styles.icon} />}
        value={
          vm.answers?.find((q) => q.id === question.id)?.answer === true
            ? 'Yes'
            : 'No'
        }
        onChange={(value) => vm.updateAnswer(value === 'Yes', question.id)}
        renderItem={(itemProps) => (
          <SingleItemSelectedDefault
            {...itemProps}
            item={itemProps}
            itemToString={(i) => String(i.item)}
          />
        )}
        renderSearchItem={({ item, itemToString, ...itemProps }) => (
          <SearchResultItem {...itemProps}>{item}</SearchResultItem>
        )}
        itemToString={itemToString}
      />
    )
  },
)

export const OCRManualQuestionsSelectInput = observer(
  function OCRManualQuestionsSelectInput({
    vm,
    selectQuestion,
  }: {
    vm: CreateOCRScopeJobsViewModel
    selectQuestion: SelectQuestionDTO
  }) {
    const itemSource = React.useMemo(
      () =>
        ItemSource.static<SelectOptionDTO>(
          selectQuestion.options,
          (item, text) => item.text.toLowerCase().includes(text.toLowerCase()),
        ),
      [selectQuestion.options],
    )
    const selectProps = useCustomSelectStateFromItemSource({
      itemSource,
    })
    const itemToString = React.useCallback(
      (item: SelectOptionDTO) => item.text,
      [],
    )

    return (
      <CustomSelect
        {...selectProps}
        multi={selectQuestion.multi}
        icon={<Icon name="dropdown" className={styles.icon} />}
        value={
          vm.answers?.find((q) => q.id === selectQuestion.id)?.answer === ''
            ? undefined
            : selectQuestion.multi
              ? (vm.answers?.find((q) => q.id === selectQuestion.id)?.answer as
                  | SelectOptionDTO[]
                  | undefined)
              : (vm.answers?.find((q) => q.id === selectQuestion.id)
                  ?.answer as SelectOptionDTO)
        }
        onChange={(value) =>
          vm.updatePotentialMultiSelectAnswer(
            value,
            selectQuestion.id,
            selectQuestion.multi,
            selectQuestion.multi ? value : undefined,
          )
        }
        placeholder={
          selectQuestion.multi ? 'Select all that apply' : 'Select one'
        }
        itemToString={itemToString}
        persist={selectQuestion.multi}
      />
    )
  },
)
